import moment from 'moment';
import {
  STATUS_REQUIRED_FIELDS,
  STATUS_FIELDS_TO_COMPARE,
  CODE_TYPE_COLORS,
} from '../constants';
import {
  formatTimestamp,
  newMomentDate,
  parseDate,
  subtractDaysFromNow,
  formatDateTime,
  formatTime,
} from '~/utils/moment';
import { buildCodeKey, buildCodeKeyFromCode } from '~/utils/codes';

const CLEAR_MAINTENANCE_FIELDS = {
  code_maintenance_id: null,
  id_code_type_maintenance: null,
  id_code_group_maintenance: null,
};

export const buildInitialStatusForm = (equipment = {}, checkboxState) => {
  const timestamp = newMomentDate().unix();

  return {
    equip_type: equipment.equip_type,
    id_equip_group: equipment.id_equip_group,
    id_equip: equipment.id_equip,
    code_type: equipment.code_type ?? -1,
    id_code_group: equipment.id_code_group ?? 0,
    id_code: equipment.id_code ?? 0,
    id_operator_group: equipment.id_operator_group ?? 0,
    id_operator: equipment.id_operator ?? 0,
    element_id: equipment.element_id ?? 0,
    point_id: equipment.point_id ?? 0,
    comment: equipment?.comment ?? '',
    sent_from_tablet: [0, 1].includes(checkboxState),
    start_timestamp: timestamp,
    start_date: equipment.start_date ?? '',
    start_time: equipment.start_time ?? '',
    has_logged_out: equipment.has_logged_out,
    duration: equipment.duration ?? '00:00:00',
    end_date: equipment.end_date ?? '',
    end_time: equipment.end_time ?? '',
    end_timestamp: equipment.end_timestamp ?? 0,
    equip_replaced: equipment.equip_replaced ?? 0,
    id: equipment?.id,
    ...CLEAR_MAINTENANCE_FIELDS,
  };
};

const sortOptionsByLabel = options => [...options].sort((a, b) => {
  const labelA = String(a?.label ?? '').trim();
  const labelB = String(b?.label ?? '').trim();

  return labelA.localeCompare(labelB, undefined, {
    sensitivity: 'base',
    numeric: true,
    ignorePunctuation: true,
  });
});

export const buildOperatorsOptions = (lookupById, form) => {
  const { code_type } = form ?? {};
  const operatorsOptions = Object.values(lookupById ?? {})
    .filter(item => item.active === 1)
    .filter(operator => (
      code_type === 0
        ? operator.type === 1 || operator.type === 2
        : operator.type === 1
    ))
    .map(operator => ({
      label: operator.name,
      value: operator.id_operator,
    }));
  return sortOptionsByLabel(operatorsOptions);
};

export const buildElementsOptions = (lookupById) => {
  const elementsOptions = Object.values(lookupById ?? {})
    .map(element => ({
      label: element.name,
      value: element.id,
    }));
  return sortOptionsByLabel(elementsOptions);
};

export const buildSubElementsOptions = (lookupById, equip, key = 'element_id') => {
  const value = equip?.[key] ?? 0;
  const subElementsOptions = Object.values(lookupById ?? {})
    .filter(subElement => subElement.id_element === value)
    .map(subElement => ({
      label: subElement.name,
      value: subElement.id,
    }));
  return sortOptionsByLabel(subElementsOptions);
};

export const buildMaintenanceCodesOptions = (lookupById) => {
  const maintenanceCodesOptions = Object.values(lookupById ?? {})
    .filter(item => item.active === 1)
    .map(maintenanceCode => ({
      label: maintenanceCode.name,
      value: maintenanceCode.id,
    }));
  return sortOptionsByLabel(maintenanceCodesOptions);
};


export const buildCodesOptions = (codes, form) => {
  const { equip_type } = form ?? {};
  const codesOptions = (codes ?? [])
    .filter(code => code.id_equip === equip_type && code.id)
    .map(code => ({
      label: code.name,
      value: buildCodeKeyFromCode(code),
      dotColor: CODE_TYPE_COLORS[code.code_type] ?? '#666666',
    }));
  return sortOptionsByLabel(codesOptions);
};


const getSelectedCode = (codesByKey, form) => codesByKey?.[buildCodeKey({
  id: form?.id_code,
  equipType: form?.equip_type,
  codeGroup: form?.id_code_group,
  codeType: form?.code_type,
})];

export const getSelectedCodeOptionValue = (codesByKey, form) => {
  const selectedCode = getSelectedCode(codesByKey, form);
  return selectedCode ? buildCodeKeyFromCode(selectedCode) : 0;
};

const isFieldValueEmpty = (key, value, codeTypeKey = 'code_type') => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim().length === 0) return true;

  if (key === codeTypeKey) {
    return typeof value === 'number' && value < 0;
  }

  return typeof value === 'number' && value === 0;
};

export const isOperatorValidForCodeType = (operator, codeType) => {
  if (!operator) return false;
  return codeType === 0
    ? operator.type === 1 || operator.type === 2
    : operator.type === 1;
};


export const clearInvalidOperator = (form, operatorsById) => {
  if (!operatorsById || !form?.id_operator) return form;

  const operator = operatorsById[form.id_operator];
  if (isOperatorValidForCodeType(operator, form.code_type)) return form;

  return { ...form, id_operator: 0, id_operator_group: 0 };
};


export const getDateErrorMessage = (value, minDateLimit, translate) => {
  const now = moment();
  const validatedValue = moment(value, 'DD/MM/YYYY HH:mm:ss', true).isValid();

  if (!validatedValue) {
    return translate('validation:InvalidDate');
  }
  if (value.isBefore(minDateLimit, 'day')) {
    return translate('validation:InvalidPeriodDate');
  }
  if (value.isAfter(now)) {
    return translate('validation:DateCantBeInTheFuture');
  }
  return null;
};

export const calculateMinEditDate = (
  userLimitEditConfig,
  enableClosure,
  closures,
) => {
  let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);
  const isEnableClosureOn = enableClosure && closures?.length > 0;

  if (isEnableClosureOn) {
    const dateNow = newMomentDate();
    const lastClosureDate = parseDate(closures[0].timestampClose, true);
    const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

    const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
    const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

    userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
      ? lockEditConfig : lockEditLastClosure.add(1, 'day');
  }

  return userLimitEdit;
};

export const applyFieldCascade = (form, field, value, lookupCtx) => {
  const { codesByKey, operatorsById, maintenanceCodesById } = lookupCtx || {};
  const next = { ...form, [field]: value };

  if (field === 'id_code') {
    // `value` é a chave única do código (ver `buildCodeKey`), pois o id sozinho não o identifica.
    const selectedCode = codesByKey?.[value];
    next.id_code = selectedCode?.id ?? 0;
    next.code_type = selectedCode?.code_type ?? -1;
    next.id_code_group = selectedCode?.id_group ?? 0;

    if (next.code_type !== 0) {
      Object.assign(next, CLEAR_MAINTENANCE_FIELDS);
    }
    Object.assign(next, clearInvalidOperator(next, operatorsById));
  }

  if (field === 'id_operator') {
    const selectedOperator = operatorsById[value];
    next.id_operator_group = selectedOperator?.id_group ?? 0;
  }

  if (field === 'code_maintenance_id') {
    const maintenance = maintenanceCodesById?.[value];
    next.id_code_type_maintenance = maintenance?.id_code_type_maintenance ?? 0;
    next.id_code_group_maintenance = maintenance?.id_code_group_maintenance ?? 0;
  }

  if (field === 'element_id') {
    next.point_id = 0;
  }

  if (field === 'start_timestamp') {
    next.start_timestamp = formatTimestamp(value);
    next.start_date = formatDateTime(value);
    next.start_time = formatTime(value);
  }

  return next;
};

export const hasMaintenanceConfig = (config) => {
  const configMaintenance = config?.find(c => c.key === 'dispatch_equipment_status_maintenance' && c.value === '1');
  return configMaintenance?.value === '1';
};

export const isFormValid = (dateError, form, lookupCtx) => {
  if (dateError) return false;

  const {
    codesByKey,
    elementsById,
    subElementsById,
    operatorsById,
  } = lookupCtx || {};


  const allFieldsFilled = STATUS_REQUIRED_FIELDS.every((key) => {
    const value = form[key];
    if (isFieldValueEmpty(key, value)) {
      return false;
    }
    if (key === 'id_code') {
      return !!getSelectedCode(codesByKey, form);
    }
    return true;
  });

  const { element_id = 0, point_id = 0, id_operator = 0 } = form || {};

  if (element_id !== 0 && !elementsById?.[element_id]) {
    return false;
  }
  if (point_id !== 0 && !subElementsById?.[point_id]) {
    return false;
  }
  if (element_id !== 0 && point_id === 0) return false;

  if (id_operator !== 0) {
    const operator = operatorsById?.[id_operator] || {};
    if (!isOperatorValidForCodeType(operator, form?.code_type)) return false;
  }

  return allFieldsFilled;
};

export const hasFormChanged = (form, originalForm) => STATUS_FIELDS_TO_COMPARE.some((key) => {
  const currentValue = form[key] ?? '';
  const originalValue = originalForm[key] ?? '';

  return currentValue !== originalValue;
});

export const isSelectedCodeInvisible = (form, codesByKey) => {
  const codeSelected = getSelectedCode(codesByKey, form) || {};
  const isInactiveOrIdleCodeType = [3, 4].includes(codeSelected?.code_type);
  const isInvisibleCode = [0, 3].includes(codeSelected?.is_visible);
  return isInactiveOrIdleCodeType || isInvisibleCode;
};
